// 主题配置 —— 2026-10-07 与官网 yeap.cloud 设计系统对齐（website/src/App.vue）
// 品牌蓝 #2357ff、正文 #0b1530、分隔线 #dfe5ef、页面底 #f5f7fb；暗色 = 官网深海军蓝。
// 控制台是高密度生产力工具：控件高度 32（antd 默认），圆角 8。
// 注意：admin 与 user 两个入口共用本文件。

const FONT_BODY = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB', 'Noto Sans SC', 'Microsoft YaHei', 'Segoe UI', sans-serif";
const FONT_CODE = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const FONT_DISPLAY = "'Sora', 'IBM Plex Sans', 'PingFang SC', 'Noto Sans SC', 'Microsoft YaHei', sans-serif";

// 品牌色（与官网 :root 同名变量一致）
const brandColors = {
  primary: '#2357ff',      // --brand
  primaryDeep: '#173fd9',  // --brand-2
  soft: '#ebf0ff',         // --brand-soft
  navy: '#071030',
  navy2: '#0d1a47',
  accent: '#ff6a2b',       // logo 橙点
  gradient: 'linear-gradient(135deg, #2357ff 0%, #7b5cff 55%, #18c2e8 100%)', // --grad，仅用于 logo / 少量强调
  gradientHover: 'linear-gradient(135deg, #173fd9 0%, #6a4ff0 55%, #10a9cc 100%)',
};

const sharedComponents = {
  Button: { borderRadius: 8, primaryShadow: 'none', defaultShadow: 'none', dangerShadow: 'none', fontWeight: 500 },
  Card: { borderRadiusLG: 10, paddingLG: 20 },
  Table: { borderRadius: 8, cellPaddingBlock: 12 },
  Modal: { borderRadiusLG: 12 },
  Tag: { borderRadiusSM: 4 },
  Badge: { dotSize: 8 },
  Statistic: { contentFontSize: 24 },
};

// 亮色主题
export const lightTheme = {
  token: {
    colorPrimary: brandColors.primary,
    colorInfo: brandColors.primary,
    colorLink: brandColors.primary,
    colorLinkHover: brandColors.primaryDeep,
    colorSuccess: '#10b981',
    colorWarning: '#f5a524',
    colorError: '#e5484d',

    borderRadius: 8,
    borderRadiusLG: 10,
    borderRadiusSM: 6,
    controlHeight: 32,

    fontFamily: FONT_BODY,
    fontFamilyCode: FONT_CODE,
    fontSize: 14,

    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgLayout: '#f5f7fb',
    colorBgSpotlight: '#0b1530',

    colorBorder: '#dfe5ef',
    colorBorderSecondary: '#edf1f7',
    colorSplit: '#edf1f7',

    colorText: '#0b1530',
    colorTextSecondary: '#4a5672',
    colorTextTertiary: '#8590a6',
    colorTextQuaternary: '#b4bccb',

    boxShadow: '0 1px 2px rgba(11, 21, 48, 0.06)',
    boxShadowSecondary: '0 8px 24px rgba(11, 21, 48, 0.10)',
    boxShadowTertiary: '0 1px 2px rgba(11, 21, 48, 0.04)',

    motionDurationFast: '0.1s',
    motionDurationMid: '0.18s',
    motionDurationSlow: '0.28s',
  },
  components: {
    ...sharedComponents,
    Table: { ...sharedComponents.Table, headerBg: '#f7f9fc', headerColor: '#4a5672', rowHoverBg: '#f5f8ff' },
    Menu: {
      itemBorderRadius: 6,
      itemMarginInline: 8,
      itemHeight: 36,
      itemSelectedBg: brandColors.soft,
      itemSelectedColor: brandColors.primary,
      itemHoverBg: '#f2f5fa',
      itemColor: '#2b3550',
      groupTitleColor: '#8590a6',
      groupTitleFontSize: 12,
      subMenuItemBg: 'transparent',
      itemBg: 'transparent',
    },
    Tabs: { itemSelectedColor: brandColors.primary, inkBarColor: brandColors.primary },
    Progress: { defaultColor: brandColors.primary },
    Layout: { headerBg: '#ffffff', siderBg: '#ffffff', bodyBg: '#f5f7fb', headerHeight: 56 },
  },
};

// 暗色主题 —— 官网深海军蓝
export const darkTheme = {
  token: {
    colorPrimary: '#4d7bff',
    colorInfo: '#4d7bff',
    colorLink: '#6f93ff',
    colorLinkHover: '#9bb4ff',
    colorSuccess: '#22c55e',
    colorWarning: '#f5a524',
    colorError: '#f0565b',

    borderRadius: 8,
    borderRadiusLG: 10,
    borderRadiusSM: 6,
    controlHeight: 32,

    fontFamily: FONT_BODY,
    fontFamilyCode: FONT_CODE,
    fontSize: 14,

    colorBgContainer: '#0d1636',
    colorBgElevated: '#121d44',
    colorBgLayout: '#060b1d',
    colorBgSpotlight: '#1b2758',

    colorBorder: '#22305e',
    colorBorderSecondary: '#18234a',
    colorSplit: '#18234a',

    colorText: '#e7ecf7',
    colorTextSecondary: '#a9b4cf',
    colorTextTertiary: '#7c88a8',
    colorTextQuaternary: '#56618a',

    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.35)',
    boxShadowSecondary: '0 8px 24px rgba(0, 0, 0, 0.45)',

    motionDurationFast: '0.1s',
    motionDurationMid: '0.18s',
    motionDurationSlow: '0.28s',
  },
  components: {
    ...sharedComponents,
    Table: { ...sharedComponents.Table, headerBg: '#111c42', headerColor: '#c4cde3', rowHoverBg: '#13204c' },
    Menu: {
      darkItemBg: '#0d1636',
      darkSubMenuItemBg: '#0d1636',
      darkItemSelectedBg: 'rgba(77, 123, 255, 0.18)',
      darkItemSelectedColor: '#9bb4ff',
      darkItemHoverBg: 'rgba(77, 123, 255, 0.10)',
      darkItemColor: '#c4cde3',
      darkGroupTitleColor: '#7c88a8',
      itemBorderRadius: 6,
      itemMarginInline: 8,
      itemHeight: 36,
      groupTitleFontSize: 12,
    },
    Tabs: { itemSelectedColor: '#6f93ff', inkBarColor: '#6f93ff' },
    Layout: { headerBg: '#0d1636', siderBg: '#0d1636', bodyBg: '#060b1d', headerHeight: 56 },
  },
};

export { brandColors };

// CSS 变量（ThemeContext 写到 :root），供自定义样式使用。旧变量名保留以兼容现有页面。
export const cssVariables = {
  light: {
    '--brand': brandColors.primary,
    '--brand-2': brandColors.primaryDeep,
    '--brand-soft': brandColors.soft,
    '--text-1': '#0b1530',
    '--text-2': '#4a5672',
    '--text-3': '#8590a6',
    '--line': '#dfe5ef',
    '--line-2': '#edf1f7',
    '--bg-1': '#ffffff',
    '--bg-2': '#f5f7fb',
    '--navy': brandColors.navy,
    '--navy-2': brandColors.navy2,
    '--font-display': FONT_DISPLAY,
    '--font-body': FONT_BODY,
    '--gradient-primary': brandColors.gradient,
    '--gradient-primary-hover': brandColors.gradientHover,
    '--glass-bg': '#ffffff',
    '--glass-border': '#dfe5ef',
    '--shadow-card': '0 1px 2px rgba(11, 21, 48, 0.06)',
    '--shadow-card-hover': '0 6px 18px rgba(11, 21, 48, 0.08)',
    '--bg-page': '#f5f7fb',
    '--bg-sidebar': '#ffffff',
    '--bg-header': '#ffffff',
  },
  dark: {
    '--brand': '#4d7bff',
    '--brand-2': '#6f93ff',
    '--brand-soft': 'rgba(77, 123, 255, 0.18)',
    '--text-1': '#e7ecf7',
    '--text-2': '#a9b4cf',
    '--text-3': '#7c88a8',
    '--line': '#22305e',
    '--line-2': '#18234a',
    '--bg-1': '#0d1636',
    '--bg-2': '#060b1d',
    '--navy': '#060b1d',
    '--navy-2': '#0d1636',
    '--font-display': FONT_DISPLAY,
    '--font-body': FONT_BODY,
    '--gradient-primary': brandColors.gradient,
    '--gradient-primary-hover': brandColors.gradientHover,
    '--glass-bg': '#0d1636',
    '--glass-border': '#22305e',
    '--shadow-card': '0 1px 2px rgba(0, 0, 0, 0.35)',
    '--shadow-card-hover': '0 6px 18px rgba(0, 0, 0, 0.45)',
    '--bg-page': '#060b1d',
    '--bg-sidebar': '#0d1636',
    '--bg-header': '#0d1636',
  },
};
